/**
 * useDashboardPages
 *
 * Manages the user's additional named dashboard pages (beyond the core /dashboard).
 * Pages live at /d/:slug and are stored in User.preferences on the backend.
 *
 * Module-level state: shared across all usages in the same session.
 * Resets on workspace change via init().
 */

import { ref } from 'vue'
import { api } from '@/lib/api'
import type { DashboardPage, SavedLayoutSnapshot, LayoutPresetWidget } from '@sevfy/shared'
import { validateDashboardSlug } from '@sevfy/shared'

// ---------------------------------------------------------------------------
// Module-level state (shared, workspace-scoped)
// ---------------------------------------------------------------------------

const pages = ref<DashboardPage[]>([])
const snapshots = ref<SavedLayoutSnapshot[]>([])
const isLoading = ref(false)
const hasLoaded = ref(false)
let currentWorkspaceId: string | null = null

// ---------------------------------------------------------------------------
// Draft tracking
//
// When a user edits widgets on a custom page (/d/:slug) without saving, those
// changes live in DashboardView's local `customPageDraftWidgets` ref.  Presets
// saved from Profile would otherwise capture only the last API-persisted state.
//
// This plain Map bridges the gap: DashboardView writes here on every widget
// mutation, and _captureSnapshot in the layout store reads here so that the
// current draft is always included in saved presets.
// ---------------------------------------------------------------------------

const _pageDrafts = new Map<string, LayoutPresetWidget[]>()

/** Mark the in-memory widget state for a custom page (before API save). */
function setPageDraft(pageId: string, widgets: LayoutPresetWidget[]): void {
  _pageDrafts.set(pageId, [...widgets])
}

/** Remove the draft for a page (called after saving or resetting). */
function clearPageDraft(pageId: string): void {
  _pageDrafts.delete(pageId)
}

/** Clear all page drafts (called when a preset is applied across all pages). */
function clearAllPageDrafts(): void {
  _pageDrafts.clear()
}

/**
 * Returns the most up-to-date widget list for a page.
 * Prefers the unsaved draft (if present) over the API-persisted state.
 */
function getEffectiveWidgets(pageId: string): LayoutPresetWidget[] {
  return _pageDrafts.get(pageId) ?? pages.value.find(p => p.id === pageId)?.widgets ?? []
}

// ---------------------------------------------------------------------------
// Init / reset
// ---------------------------------------------------------------------------

function init(workspaceId: string) {
  if (currentWorkspaceId === workspaceId) return
  currentWorkspaceId = workspaceId
  pages.value = []
  snapshots.value = []
  hasLoaded.value = false
  _pageDrafts.clear()
}

async function fetchPages(force = false): Promise<void> {
  if (!force && hasLoaded.value) return
  isLoading.value = true
  try {
    const res = await api.get<{ dashboardPages: DashboardPage[] }>('/dashboards')
    pages.value = res.dashboardPages
    hasLoaded.value = true
  } catch {
    // silent - non-fatal
  } finally {
    isLoading.value = false
  }
}

// ---------------------------------------------------------------------------
// CRUD
// ---------------------------------------------------------------------------

async function createPage(
  name: string,
  slug?: string,
  description?: string
): Promise<DashboardPage> {
  const res = await api.post<{ dashboardPage: DashboardPage }>('/dashboards', {
    name,
    slug,
    description,
  })
  pages.value = [...pages.value, res.dashboardPage]
  return res.dashboardPage
}

async function updatePage(
  id: string,
  patch: Partial<Pick<DashboardPage, 'name' | 'slug' | 'description' | 'navOrder' | 'navVisible' | 'widgets'>>
): Promise<DashboardPage> {
  // Verify the page still exists locally before hitting the API - guards against
  // stale refs after a preset apply recreated pages with new IDs.
  const localPage = pages.value.find(p => p.id === id)
  if (!localPage) throw new Error(`Dashboard page ${id} not found locally`)
  const res = await api.patch<{ dashboardPage: DashboardPage }>(`/dashboards/${id}`, patch)
  pages.value = pages.value.map(p => p.id === id ? res.dashboardPage : p)
  return res.dashboardPage
}

async function deletePage(id: string): Promise<void> {
  await api.delete(`/dashboards/${id}`)
  pages.value = pages.value.filter(p => p.id !== id)
}

async function savePageWidgets(id: string, widgets: LayoutPresetWidget[]): Promise<void> {
  await updatePage(id, { widgets })
}

async function reorderPages(orderedIds: string[]): Promise<void> {
  const res = await api.post<{ dashboardPages: DashboardPage[] }>('/dashboards/reorder', {
    order: orderedIds,
  })
  pages.value = res.dashboardPages
}

// ---------------------------------------------------------------------------
// Saved layout snapshots
// ---------------------------------------------------------------------------

async function fetchSnapshots(force = false): Promise<void> {
  if (!force && snapshots.value.length > 0) return
  try {
    const res = await api.get<{ snapshots: SavedLayoutSnapshot[] }>('/dashboards/snapshots')
    snapshots.value = res.snapshots
  } catch {
    // silent
  }
}

async function saveSnapshot(
  name: string,
  scope: 'single_page' | 'all_pages',
  widgets: LayoutPresetWidget[],
  dashboardPageId?: string | null,
  allPages?: DashboardPage[],
  navOrder?: string[],
  navHidden?: string[]
): Promise<SavedLayoutSnapshot> {
  const res = await api.post<{ snapshot: SavedLayoutSnapshot }>('/dashboards/snapshots', {
    name,
    scope,
    dashboardPageId: dashboardPageId ?? null,
    widgets,
    allPages,
    navOrder,
    navHidden,
  })
  snapshots.value = [...snapshots.value, res.snapshot]
  return res.snapshot
}

async function deleteSnapshot(id: string): Promise<void> {
  await api.delete(`/dashboards/snapshots/${id}`)
  snapshots.value = snapshots.value.filter(s => s.id !== id)
}

// ---------------------------------------------------------------------------
// Lookups
// ---------------------------------------------------------------------------

function getBySlug(slug: string): DashboardPage | undefined {
  return pages.value.find(p => p.slug === slug)
}

function getById(id: string): DashboardPage | undefined {
  return pages.value.find(p => p.id === id)
}

// ---------------------------------------------------------------------------
// Slug helpers
// ---------------------------------------------------------------------------

function toSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 60)
}

function slugError(slug: string): string | null {
  return validateDashboardSlug(slug)
}

function isSlugTaken(slug: string, excludeId?: string): boolean {
  return pages.value.some(p => p.slug === slug && p.id !== excludeId)
}

// ---------------------------------------------------------------------------
// Composable export
// ---------------------------------------------------------------------------

export function useDashboardPages() {
  return {
    // State
    pages,
    snapshots,
    isLoading,
    hasLoaded,

    // Init
    init,
    fetchPages,
    fetchSnapshots,

    // CRUD
    createPage,
    updatePage,
    deletePage,
    savePageWidgets,
    reorderPages,

    // Snapshots
    saveSnapshot,
    deleteSnapshot,

    // Lookups
    getBySlug,
    getById,

    // Slug helpers
    toSlug,
    slugError,
    isSlugTaken,

    // Draft tracking (used by DashboardView to keep presets in sync)
    setPageDraft,
    clearPageDraft,
    clearAllPageDrafts,
    getEffectiveWidgets,
  }
}
